import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { EnderecoType, EnderecosType } from '@mobile/app/shared/types/enderecos.types';
import { Local } from '@mobile/app/shared/types/pdv.types';
import { User } from '@mobile/app/shared/types/user.types';
import { Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UsersFirebaseService {
  constructor(private afs: AngularFirestore) {}

  /**
   * Busca um usuário pelo seu UID.
   * @param uid O UID do usuário.
   * @returns Observable<User>
   */
  getUserValueChanges(uid: string): Observable<User> {
    return this.afs
      .collection('Users')
      .doc<User>(uid)
      .valueChanges({ idField: 'uid' }).pipe(
        // Filtra para garantir que o resultado seja um objeto e não undefined/null
        filter(user => !!user && typeof user === 'object'),
        // Opcional: Garante que o TypeScript entenda que o valor não é mais nulo
        map(user => user as User)
      );
  }

  /**
   * Busca um usuário pelo seu UID (Promise).
   * @param uid O UID do usuário.
   * @returns Promise<User>
   */
  async getUser(uid: string): Promise<User | undefined> {
    const doc = await this.afs.collection('Users').doc<User>(uid).get().toPromise();
    if (doc.exists) {
      return { uid: doc.id, ...doc.data() } as User;
    }
    return undefined;
  }

  /**
   * Atualiza os dados de um usuário.
   * @param uid O UID do usuário.
   * @param data Os dados a serem atualizados.
   * @returns Promise<void>
   */
  updateUser(uid: string, data: Partial<User>): Promise<void> {
    return this.afs.collection('Users').doc(uid).update(data);
  }

  /**
   * Cria um novo documento de usuário no Firestore.
   * @param uid O UID do usuário.
   * @param data Os dados do usuário a serem salvos.
   * @returns Promise<void>
   */
  createUser(uid: string, data: Omit<User, 'uid'>): Promise<void> {
    // Usamos 'set' para criar o documento com um ID específico (o uid da autenticação)
    return this.afs.collection('Users').doc(uid).set(data);
  }

  /**
   * Busca todos os usuários (clientes).
   * @returns Promise<User[]>
   */
  async getAllClientes(): Promise<User[]> {
    const querySnapshot = await this.afs
      .collection('Users', (ref) =>
        ref.where('roles', 'array-contains', 'cliente')
      )
      .get()
      .pipe(first())
      .toPromise();
    const users: User[] = [];
    querySnapshot.forEach((doc) => {
      users.push({ uid: doc.id, ...doc.data() } as User);
    });
    return users;
  }

  /**
   * Converte a estrutura de endereços do app para a estrutura de locais do PDV.
   * @param enderecos A estrutura de endereços do app.
   * @returns Um array de Local.
   */
  private mapEnderecosToLocais(enderecos: EnderecosType): Local[] {
    if (!enderecos || !enderecos.cadastrados) {
      return [];
    }

    return enderecos.cadastrados.map((end: EnderecoType) => {
      const enderecoCompleto = `${end.rua}, ${end.numero}${
        end.complemento ? ' - ' + end.complemento : ''
      }`;

      return {
        local_name: end.nome,
        endereco: enderecoCompleto,
        bairro: end.bairro,
        referencia: end.referencia,
        id: end.id,
      };
    });
  }

  /**
   * Migra os endereços de um usuário para a nova estrutura dentro do documento do usuário.
   * @param uid O UID do usuário.
   * @param enderecos A estrutura de endereços antiga.
   * @returns Promise<void>
   */
  migrateUserAddresses(uid: string, enderecos: EnderecosType): Promise<void> {
    const locais = this.mapEnderecosToLocais(enderecos);
    const dataToUpdate: Partial<User> = {
      locais,
      endereco_selecionado_id: enderecos.selecionado,
    };
    return this.updateUser(uid, dataToUpdate);
  }
}
